import React from 'react';

import { useReport } from './hooks/use-context';
import { SectionData } from './types';

export function Table() {
  const { body } = useReport();

  const sections = body?.sections?.filter(
    (section) => section.section_name !== 'Informações',
  );

  const truncateTo2Decimals = (value: number | null | undefined): string => {
    if (value == null) return '0';
    const str = value.toString();
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) return str + ',00';
    const truncated = str.slice(0, dotIndex + 3).padEnd(dotIndex + 3, '0');
    return truncated.replace('.', ',');
  };

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 24fr 4fr 2fr 2fr 16fr 10fr',
  };

  const HeadColumns = ({
    label,
    isLast,
    hideBorderRight,
    showBorderLeft,
  }: {
    label: string;
    isLast?: boolean;
    hideBorderRight?: boolean;
    showBorderLeft?: boolean;
  }) => (
    <div
      style={{
        backgroundColor: '#d3d3d3',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        borderLeft: showBorderLeft ? '1.1px #000 solid' : 'none',
        borderRight: !isLast && !hideBorderRight ? '1.1px #000 solid' : 'none',
        borderBottom: '1.1px #000 solid',
        fontSize: '9px',
      }}
    >
      {label}
    </div>
  );

  const ValueCells = ({
    label,
    isLast,
    hideBorderRight,
    gridColumn,
    gridRow,
  }: {
    label: string;
    isLast?: boolean;
    hideBorderRight?: boolean;
    gridColumn?: string;
    gridRow?: number;
  }) => (
    <div
      style={{
        gridColumn,
        gridRow,
        backgroundColor: '#fff',
        padding: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        borderRight: !hideBorderRight ? '1.1px #000 solid' : 'none',
        borderBottom: !isLast ? '1.1px #000 solid' : 'none',
        fontSize: '9px',
      }}
    >
      {label}
    </div>
  );

  const Section = ({
    section,
    isEven,
    isFirst,
    isLast,
  }: {
    section: SectionData;
    isEven?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
  }) => {
    const orderedQuestions = section?.questions
      .sort((a, b) => a.position - b.position)
      .filter((question) => question?.type === 'radio');

    const numRows = orderedQuestions?.length || 1;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 24fr 4fr 2fr 2fr 16fr 10fr',
          breakInside: !isFirst ? 'avoid' : undefined,
        }}
      >
        <div
          style={{
            gridColumn: '1',
            gridRow: `1 / span ${numRows}`,
            backgroundColor: isEven ? '#d3d3d3' : '#fff',
            borderRight: '1.1px #000 solid',
            borderBottom: '1.1px #000 solid',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              transform: 'rotate(-90deg)',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              width: '100%',
              height: `${20 + section.section_name.length * 6}px`,
              margin: 0,
            }}
          >
            {section.section_name}
          </div>
        </div>

        {orderedQuestions?.map((question, index) => {
          const getText = () => {
            if (
              question.answers[0]?.option === 'Não Conforme' ||
              question.answers[0]?.option === 'Sim'
            ) {
              const globalQuestionIndex = section?.questions.findIndex(
                (item) => item.question_id === question.question_id,
              );

              if (section?.questions[globalQuestionIndex + 1]?.type === 'text')
                return section.questions[globalQuestionIndex + 1]
                  ?.answers?.[0]?.content;
            }
          };

          const row = index + 1;

          return (
            <React.Fragment key={index}>
              <ValueCells gridColumn="2" gridRow={row} label={question.name} />
              <ValueCells
                gridColumn="3"
                gridRow={row}
                label={
                  question.answers[0]?.option !== 'Não Aplicável' &&
                  (question.score_obtained > 0 ||
                    question.answers[0]?.option === 'Conforme')
                    ? 'X'
                    : ''
                }
              />
              <ValueCells
                gridColumn="4"
                gridRow={row}
                label={
                  question.answers[0]?.option === 'Não Conforme' ||
                  (question.answers[0]?.option !== 'Não Aplicável' &&
                    question.score_obtained === 0 &&
                    question.answers[0]?.option !== 'Conforme')
                    ? 'X'
                    : ''
                }
              />
              <ValueCells
                gridColumn="5"
                gridRow={row}
                label={
                  question.answers[0]?.option === 'Não Aplicável' ? 'X' : ''
                }
              />
              <ValueCells
                gridColumn="6"
                gridRow={row}
                label={getText() || question.answers[0]?.comment || ''}
                hideBorderRight
              />
            </React.Fragment>
          );
        })}

        <div
          style={{
            gridColumn: '7',
            gridRow: `1 / span ${numRows}`,
            backgroundColor: '#fff',
            borderLeft: '1.1px #000 solid',
            borderBottom: isLast ? '1.1px #000 solid' : '',
            overflow: 'inherit',
          }}
        >
          <div
            style={{
              display: 'flex',
              transform: 'rotate(-90deg)',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              width: '100%',
              paddingRight: '40px',
              margin: 0,
              fontWeight: 'bold',
            }}
          >
            {isFirst ? truncateTo2Decimals(body?.percent_score) : ''}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Headers */}
      <div style={gridStyles}>
        <div
          style={{
            backgroundColor: '#d3d3d3',
            borderRight: '1.1px #000 solid',
            borderBottom: '1.1px #000 solid',
            height: '30px',
          }}
        ></div>
        <HeadColumns label="Items" />
        <HeadColumns label="Conforme" />
        <HeadColumns label="NC" />
        <HeadColumns label="NA" />
        <HeadColumns label="Observações" hideBorderRight />
        <HeadColumns label="Geral" isLast showBorderLeft />
      </div>

      {sections
        ?.sort((a, b) => a.position - b.position)
        .map((section, index, sortedSections) => (
          <Section
            key={`section_${index}`}
            section={section}
            isEven={index % 2 === 0}
            isFirst={index === 0}
            isLast={index === sortedSections.length - 1}
          />
        ))}

      <div
        style={{
          padding: '4px',
          fontSize: '10px',
          borderBottom: '1.1px #000 solid',
        }}
      >
        <div style={{ fontWeight: 'bold' }}>Observações gerais:</div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {
            body?.sections
              ?.find((section) => section.section_name === 'Outros...')
              ?.questions.find(
                (question) => question.name === 'Observações gerais:',
              )?.answers[0]?.content
          }
        </div>
      </div>
    </div>
  );
}
